import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Avatar, Typography, Spin, Row, Col } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import * as FaIcons from 'react-icons/fa';
import Sidebar from '../components/sidebar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatIA.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const ChatIA = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! 👋 Soy tu asistente virtual de KATANA. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = (smooth = false) => {
    // Método 1: Scroll usando el contenedor directamente
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const maxScroll = container.scrollHeight - container.clientHeight;
      
      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }
      
      // Forzar scroll si no funcionó
      setTimeout(() => {
        if (container.scrollTop < maxScroll - 10) {
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    }
    
    // Método 2: Usar scrollIntoView como respaldo
    if (messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ 
          behavior: smooth ? 'smooth' : 'auto', 
          block: 'nearest',
          inline: 'nearest'
        });
      }, 10);
    }
  };

  useEffect(() => {
    if (!isUserScrolling) {
      // Usar requestAnimationFrame para asegurar que el DOM se haya actualizado
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToBottom(true);
        }, 50);
      });
    }
  }, [messages, isUserScrolling]);

  // Efecto adicional para hacer scroll cuando termine de cargar
  useEffect(() => {
    if (!isLoading && !isUserScrolling) {
      // Múltiples intentos para asegurar que el scroll funcione
      const timer1 = setTimeout(() => {
        scrollToBottom(true);
      }, 100);
      
      const timer2 = setTimeout(() => {
        scrollToBottom(true);
      }, 300);
      
      const timer3 = setTimeout(() => {
        scrollToBottom(false); // Sin animación para forzar el scroll
      }, 500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isLoading, isUserScrolling]);

  // Cargar información del usuario
  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUserData(user);
    }
  }, []);

  // Detectar scroll manual del usuario
  const handleScroll = (e) => {
    const container = e.target;
    const threshold = 50; // Umbral de 50px desde el fondo
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    
    if (isAtBottom) {
      setIsUserScrolling(false);
    } else {
      setIsUserScrolling(true);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsUserScrolling(false); // Forzar scroll al final cuando se envía mensaje

    // Scroll inmediatamente después de agregar el mensaje del usuario
    setTimeout(() => {
      scrollToBottom(true);
    }, 150);

    try {
      // Simular respuesta del chatbot
      const botResponse = await generateBotResponse(inputMessage);
      
      setTimeout(() => {
        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setIsLoading(false);
        setIsUserScrolling(false); // Asegurar que el scroll funcione
        
        // Scroll después de la respuesta del bot con múltiples intentos
        setTimeout(() => {
          scrollToBottom(true);
        }, 150);
        
        // Scroll adicional después de que el contenido se haya renderizado completamente
        setTimeout(() => {
          scrollToBottom(true);
        }, 300);
      }, 1000);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      
      setTimeout(() => {
        scrollToBottom(true);
      }, 150);
    }
  };

  const generateBotResponse = async (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Obtener contenidos de archivos subidos para enriquecer las respuestas
    let contenidosArchivos = '';
    try {
      const response = await fetch('https://api.mrcirp.com/public/archivos-ia-contenidos');
      
      if (!response.ok) {
        // Continuar sin los contenidos si hay error
      } else {
        // Verificar que el contenido sea JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const text = await response.text();
          
          // Intentar parsear el JSON
          let archivos;
          try {
            archivos = JSON.parse(text);
          } catch (parseError) {
            // Continuar sin los contenidos
            archivos = [];
          }
          
          if (Array.isArray(archivos) && archivos.length > 0) {
            // Filtrar solo los que tienen contenido_texto válido
            const archivosConContenido = archivos.filter(a => a && a.contenido_texto && a.contenido_texto.length > 50);
            
            if (archivosConContenido.length > 0) {
              contenidosArchivos = '\n\n═══════════════════════════════════════════════════════════\n';
              contenidosArchivos += '📚 INFORMACIÓN DE DOCUMENTOS OFICIALES DISPONIBLES:\n';
              contenidosArchivos += '═══════════════════════════════════════════════════════════\n';
              contenidosArchivos += '⚠️ ESTA ES LA INFORMACIÓN MÁS IMPORTANTE Y ACTUALIZADA.\n';
              contenidosArchivos += '⚠️ DEBES USAR ESTA INFORMACIÓN COMO FUENTE PRINCIPAL PARA RESPONDER.\n\n';
              
              archivosConContenido.forEach((archivo, index) => {
                // Aumentar el límite a 15000 caracteres por documento para incluir más información
                const contenidoLimitado = archivo.contenido_texto.length > 15000 
                  ? archivo.contenido_texto.substring(0, 15000) + '\n... [Texto truncado - hay más contenido en el documento original]'
                  : archivo.contenido_texto;
                
                contenidosArchivos += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
                contenidosArchivos += `📄 DOCUMENTO ${index + 1}: ${archivo.nombre || 'Sin nombre'}\n`;
                contenidosArchivos += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
                contenidosArchivos += `${contenidoLimitado}\n`;
              });
              
              contenidosArchivos += '\n═══════════════════════════════════════════════════════════\n';
              contenidosArchivos += 'FIN DE LA INFORMACIÓN DE DOCUMENTOS\n';
              contenidosArchivos += '═══════════════════════════════════════════════════════════\n';
            }
          }
        }
      }
    } catch (error) {
      // Continuar sin los contenidos si hay error
    }
    
    // Respuestas rápidas solo para saludos muy simples (sin consultar IA)
    // SOLO si el mensaje es EXACTAMENTE un saludo sin preguntas adicionales
    if (userData) {
      const userName = userData.nombreuser || userData.nombre || 'Usuario';
      
      // Limpiar el mensaje y verificar si es SOLO un saludo
      const mensajeLimpio = lowerMessage.trim().replace(/[.,!?]/g, '').trim();
      const palabras = mensajeLimpio.split(/\s+/);
      
      // Saludos exactos que deben coincidir completamente
      const saludosExactos = [
        'hola', 
        'hi', 
        'hello', 
        'buenos días', 
        'buenos dias', 
        'buen día', 
        'buen dia',
        'buenas',
        'saludos'
      ];
      
      // Solo responder con saludo si:
      // 1. El mensaje limpio coincide exactamente con un saludo, O
      // 2. Tiene máximo 2 palabras y la primera es un saludo
      const esSoloSaludo = saludosExactos.includes(mensajeLimpio) || 
                          (palabras.length <= 2 && saludosExactos.includes(palabras[0]));
      
      // NO responder con saludo si hay palabras de pregunta
      const palabrasPregunta = ['qué', 'que', 'cuál', 'cual', 'cómo', 'como', 'cuándo', 'cuando', 'dónde', 'donde', 'quién', 'quien', 'cuánto', 'cuanto', 'cuánta', 'cuanta', 'cuántos', 'cuantos', 'cuántas', 'cuantas', 'información', 'informacion', 'novedades', 'ayuda', 'ayudar'];
      const tienePregunta = palabrasPregunta.some(palabra => lowerMessage.includes(palabra));
      
      if (esSoloSaludo && !tienePregunta && palabras.length <= 2) {
        return `¡Hola ${userName}! 😊 Me da mucho gusto saludarte. ¿En qué puedo ayudarte hoy?`;
      }
    }
    
    // Para todas las demás preguntas, usar la API de Gemini con los contenidos de los archivos
    try {
      // Construir el prompt base
      let prompt = `Eres un asistente de IA especializado en la plataforma KATANA (Constructor de Ingresos Residuales Perpetuos) y KATANA Academy.

INFORMACIÓN GENERAL SOBRE KATANA:
- KATANA es una plataforma educativa que ayuda a crear y gestionar campañas de marketing digital
- Permite crear campañas de video y de lanzamiento
- Los usuarios pueden gestionar sus datos, crear landings y mucho más
- KATANA Academy contiene cursos y módulos educativos`;

      // Agregar contenidos de archivos si están disponibles
      if (contenidosArchivos && contenidosArchivos.length > 50) {
        // Agregar los contenidos ANTES de las instrucciones generales
        prompt += `\n\n${contenidosArchivos}`;
      }
      
      // Limitar el tamaño del prompt para evitar errores (máximo ~50k caracteres para modelos más grandes)
      if (prompt.length > 50000) {
        prompt = prompt.substring(0, 50000) + '\n\n[Prompt truncado por tamaño]';
      }

      // Instrucciones más enfáticas sobre usar los documentos
      if (contenidosArchivos && contenidosArchivos.length > 50) {
        // Extraer palabras clave de la pregunta del usuario para búsqueda
        const palabrasClave = userMessage.toLowerCase().split(/\s+/).filter(p => p.length > 3);
        
        prompt += `\n\n╔═══════════════════════════════════════════════════════════════╗
║              ⚠️ INSTRUCCIONES CRÍTICAS - LEER PRIMERO ⚠️          ║
╚═══════════════════════════════════════════════════════════════╝

🚨 REGLAS OBLIGATORIAS - NO IGNORAR:

1. ⭐ PRIORIDAD #1: Los DOCUMENTOS OFICIALES mostrados arriba contienen información REAL y ACTUALIZADA. 
   DEBES usar esa información como tu ÚNICA fuente de verdad para responder.

2. 🔍 BUSCAR PRIMERO: Antes de decir "no tengo información", DEBES buscar en los documentos 
   las siguientes palabras clave relacionadas con la pregunta: ${palabrasClave.join(', ')}

3. ✅ SI ENCUENTRAS INFORMACIÓN: Usa EXACTAMENTE esa información de los documentos. 
   Copia y parafrasea el contenido relevante. 
   ⚠️ IMPORTANTE: NO menciones los nombres de los documentos en tu respuesta. 
   Solo usa la información como si fuera tu conocimiento interno.

4. ❌ NO INVENTAR: Si NO encuentras información en los documentos, di claramente:
   "No encontré información específica sobre esto en los documentos disponibles."

5. 📋 EJEMPLO: Si el usuario pregunta sobre "novedades 2026", busca en los documentos 
   las palabras: "2026", "novedades", "nuevo", "lanzamiento", "actualización", etc.

6. 🎯 RESPUESTA REQUERIDA: Tu respuesta DEBE comenzar buscando en los documentos. 
   Si encuentras algo, úsalo directamente sin mencionar que viene de un documento. 
   Responde como si esa información fuera parte de tu conocimiento sobre KATANA.

7. 🚫 NO MENCIONAR: NO digas "según el documento X" o "en el documento Y". 
   Simplemente proporciona la información como si fuera conocimiento directo.

Pregunta del usuario: "${userMessage}"

PALABRAS CLAVE A BUSCAR EN DOCUMENTOS: ${palabrasClave.join(', ')}

INSTRUCCIÓN FINAL: Lee TODOS los documentos mostrados arriba. Busca información sobre "${userMessage}". 
Si encuentras algo relacionado, úsalo como respuesta DIRECTA sin mencionar que viene de documentos. 
Si no encuentras nada, entonces di que no está disponible.`;
      } else {
        prompt += `\n\nINSTRUCCIONES PARA RESPONDER:
1. Responde de manera clara y profesional
2. Mantén un tono amigable
3. Usa emojis de manera moderada (1-2 por respuesta)
4. Sé conciso pero completo

Pregunta del usuario: ${userMessage}

Responde de manera útil y precisa:`;
      }

      // Primero intentar listar modelos disponibles
      let availableModel = null;
      try {
        const listUrl = `https://generativelanguage.googleapis.com/v1beta/models?key=AIzaSyDYq-ckty06l8SaG1gz7hUTvhuLO-3AfqQ`;
        const listResponse = await fetch(listUrl);
        if (listResponse.ok) {
          const listData = await listResponse.json();
          if (listData.models && listData.models.length > 0) {
            // Buscar un modelo que soporte generateContent
            const generateContentModel = listData.models.find(m => 
              m.supportedGenerationMethods && 
              m.supportedGenerationMethods.includes('generateContent')
            );
            if (generateContentModel) {
              availableModel = generateContentModel.name.replace('models/', '');
            }
          }
        }
      } catch (e) {
        // Usar lista predeterminada si falla
      }
      
      // Intentar con diferentes modelos y versiones de API de Gemini
      const apiConfigs = availableModel 
        ? [{ model: availableModel, version: 'v1beta' }]
        : [
            { model: 'gemini-pro', version: 'v1' },
            { model: 'gemini-1.5-flash-latest', version: 'v1beta' },
            { model: 'gemini-1.5-pro-latest', version: 'v1beta' },
            { model: 'gemini-pro', version: 'v1beta' },
            { model: 'gemini-1.0-pro', version: 'v1beta' },
            { model: 'gemini-1.0-pro-latest', version: 'v1beta' },
          ];
      
      let lastError = null;
      
      for (const config of apiConfigs) {
        try {
          // Construir URL según la versión
          let apiUrl;
          if (config.version === 'v1') {
            apiUrl = `https://generativelanguage.googleapis.com/v1/models/${config.model}:generateContent?key=AIzaSyDYq-ckty06l8SaG1gz7hUTvhuLO-3AfqQ`;
          } else {
            apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${config.model}:generateContent?key=AIzaSyDYq-ckty06l8SaG1gz7hUTvhuLO-3AfqQ`;
          }
          
          console.log(`Intentando con modelo: ${config.model}, versión: ${config.version}`);
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              contents: [{
                parts: [{
                  text: prompt
                }]
              }],
              generationConfig: {
                temperature: 0.7,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: 2048,
              }
            })
          });

          const responseText = await response.text();
          let data;
          
          try {
            data = JSON.parse(responseText);
          } catch (e) {
            continue;
          }

          if (response.ok) {
            if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
              return data.candidates[0].content.parts[0].text;
            } else if (data.error) {
              lastError = data.error.message || 'Error desconocido de Gemini';
              continue; // Intentar con el siguiente modelo
            }
          } else {
            if (data.error) {
              lastError = data.error.message || `Error ${response.status}`;
            } else {
              lastError = `Error ${response.status}`;
            }
            continue; // Intentar con el siguiente modelo
          }
        } catch (fetchError) {
          lastError = fetchError.message;
          continue; // Intentar con el siguiente modelo
        }
      }
      
      // Si todos los modelos fallaron, lanzar el último error
      if (lastError) {
        throw new Error(`No se pudo conectar con Gemini API. Último error: ${lastError}`);
      }
    } catch (error) {
      // Continuar con respuesta por defecto si falla
    }

    // Respuesta por defecto si falla la API
    return `Gracias por tu mensaje. 😊 Estoy aquí para ayudarte con cualquier consulta sobre KATANA. Puedo asistirte con:\n\n• Creación y gestión de campañas\n• Navegación en la plataforma\n• Resolución de problemas\n• Información sobre funcionalidades${contenidosArchivos ? '\n• Información de documentos subidos' : ''}\n\n¿Hay algo específico en lo que pueda ayudarte? 🤖`;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      sendMessage();
      return false;
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const clearChat = () => {
    setMessages([{
      id: 1,
      text: "¡Hola! 👋 Soy tu asistente virtual de KATANA. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }]);
  };

  return (
    <Sidebar>
      <div className="chat-ia-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={18}>
            <Card className="chat-ia-card">
              <div className="chat-ia-header">
                <div className="chat-ia-header-content">
                  <Avatar 
                    icon={<RobotOutlined />} 
                    size="large"
                    style={{ backgroundColor: '#6c757d' }}
                  />
                  <div className="chat-ia-header-text">
                    <Title level={4} style={{ margin: 0, color: '#333' }}>
                      Asistente Virtual KATANA
                    </Title>
                    <Text type="secondary">
                      En línea • Siempre disponible para ayudarte
                    </Text>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={clearChat}
                  title="Limpiar conversación"
                />
                </div>
              </div>

              {/* Mensajes */}
              <div className="chat-ia-messages-container">
                {isUserScrolling && (
                  <div className="chat-ia-scroll-to-bottom">
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<FaIcons.FaArrowDown />}
                      onClick={() => {
                        setIsUserScrolling(false);
                        scrollToBottom(true);
                      }}
                      size="small"
                      style={{
                        backgroundColor: '#1976d2',
                        borderColor: '#1976d2',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </div>
                )}
                <div 
                  className="chat-ia-messages" 
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                >
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`chat-ia-message ${message.sender}`}
                    >
                      <div className="chat-ia-message-content">
                        <div className="chat-ia-message-avatar">
                        {message.sender === 'bot' ? (
                          <Avatar 
                            icon={<RobotOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#6c757d' }}
                          />
                        ) : (
                          <Avatar 
                            icon={<UserOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#1976d2' }}
                          />
                        )}
                        </div>
                        <div className="chat-ia-message-bubble">
                          <Text style={{ whiteSpace: 'pre-line' }}>{message.text}</Text>
                          <div className="chat-ia-message-time">
                            {formatTime(message.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="chat-ia-message bot">
                      <div className="chat-ia-message-content">
                        <div className="chat-ia-message-avatar">
                          <Avatar 
                            icon={<RobotOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#6c757d' }}
                          />
                        </div>
                        <div className="chat-ia-message-bubble">
                          <Spin size="small" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input del chatbot */}
              <div className="chat-ia-input">
                <TextArea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      sendMessage();
                    }
                  }}
                  placeholder="Escribe tu mensaje aquí..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{ 
                    resize: 'none',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px'
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  size="large"
                  style={{ 
                    backgroundColor: '#1976d2',
                    borderColor: '#1976d2',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={6}>
            <Card title="💡 Consejos" className="chat-ia-tips">
              <div className="chat-ia-tips-content">
                <div className="tip-item">
                  <Text strong>🎯 Campañas</Text>
                  <Text type="secondary">Pregúntame sobre cómo crear campañas de video o lanzamiento</Text>
                </div>
                <div className="tip-item">
                  <Text strong>📊 Datos</Text>
                  <Text type="secondary">Te ayudo a entender tus estadísticas y métricas</Text>
                </div>
                <div className="tip-item">
                  <Text strong>🔧 Soporte</Text>
                  <Text type="secondary">Resuelvo problemas técnicos y dudas generales</Text>
                </div>
                <div className="tip-item">
                  <Text strong>📚 Aprendizaje</Text>
                  <Text type="secondary">Te guío en el uso de todas las funcionalidades</Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </Sidebar>
  );
};

export default ChatIA;
